import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { MsalProvider } from '@azure/msal-react';
import { App } from './App';
import { msalInstance, msalReady } from './lib/msal';
import './styles.css';
import './site-header.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* Bumped from 60 s → 5 min. Most SPA data (workspaces, admin flag,
         approval status, cooldown, latest-summary) rarely changes; brief
         navigations should render instantly from cache instead of firing
         another API call. Mutations invalidate the relevant keys, so
         staleness after a real change is bounded. */
      staleTime: 5 * 60_000,
      /* Keep cached data around for an hour so back-navigation and tab
         restore hit cache first. */
      gcTime: 60 * 60_000,
      refetchOnWindowFocus: true,
      /* Answers that won't change on a retry (4xx, other than a timeout or "too many") aren't retried. */
      retry: (failures, error) => {
        const s = (error as { status?: number })?.status;
        if (s && s >= 400 && s < 500 && s !== 408 && s !== 429) return false;
        return failures < 3;
      },
    },
  },
});

/* After a deploy, a tab still running the old app asks for script files that no longer exist: reload once
   (at most once a minute), which fetches the new app (audit 0.18). */
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem('preload-reload') || 0);
    if (Date.now() - last > 60_000) {
      sessionStorage.setItem('preload-reload', String(Date.now()));
      event.preventDefault();
      window.location.reload();
    }
  } catch {
    /* storage blocked: the page's error message offers a reload */
  }
});

/* Persist React Query cache to sessionStorage. Tab restore and quick
   back-navigation now render instantly from cache; a background refresh
   still runs to catch upstream changes. sessionStorage (not localStorage)
   because per-tab is the right lifetime for a signed-in workflow —
   closing the tab clears cache alongside the sign-in session. */
/* Right after signing out (lib/msal.ts sets the flag), drop what the last person's session kept. */
try {
  if (sessionStorage.getItem('signed-out')) sessionStorage.removeItem('rq-cache:v1');
} catch {
  /* storage blocked */
}
const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
  key: 'rq-cache:v1',
  throttleTime: 1000,
});

/* MSAL v3 requires initialize() (and a single handleRedirectPromise)
   to complete BEFORE any component calls msalInstance methods.
   We block render on that — the lightweight init usually finishes in
   well under a frame. */
/* If the sign-in library can't start in this browser, say so rather than leave a blank page (audit 0.18). */
function startupFailed(e: unknown) {
  console.error('[app] could not start:', e);
  const root = document.getElementById('root');
  if (root)
    root.innerHTML =
      '<div style="max-width:32rem;margin:6rem auto;padding:0 1.5rem;font-family:Georgia,serif;text-align:center;color:#3a3530">' +
      '<h1 style="font-size:1.6rem;margin:0 0 1rem;color:#1f1c18">This page couldn’t start.</h1>' +
      '<p style="margin:0 0 1.5rem;line-height:1.5">Reload it, or try another browser. Private windows that block site storage can stop sign-in from starting.</p>' +
      '<p><a href="/" style="color:#5b4a2f">Reload</a> &middot; <a href="https://prajnainnovates.com/" style="color:#5b4a2f">prajnainnovates.com</a></p></div>';
}

msalReady.then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            /* One-hour cache freshness for tab restore. */
            maxAge: 60 * 60_000,
            /* Bust old caches on new deploys — bumping this string
               invalidates every visitor's persisted state. */
            buster: 'v2', // bumped when the API adds fields cached pages rely on (checklist can_answer, billing_answered_at)
            /* A Paid Members article's full text is never kept in the tab's storage. */
            dehydrateOptions: { shouldDehydrateQuery: (q) => defaultShouldDehydrateQuery(q) && q.queryKey[0] !== 'article' },
          }}
        >
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PersistQueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}, startupFailed);
